/**
 * Methodology — how each figure on the page is worked out
 * =======================================================
 * Usability review of 23 October 2026, item 5: the working notes go here or
 * behind an ⓘ, in plain English, with no table or field names and no ticket
 * numbers. The notes this replaced named five internal tables; a reader
 * cannot act on any of them.
 */
import React from "react";
import { amount, money } from "../../components/format";
import { CAPITAL_FLOOR_USD, CAPITAL_SHARE } from "./navLogic";

function Row({ label, basis, children }) {
  return (
    <div className="nvp-method-row">
      <div className="nvp-method-head">
        <h3>{label}</h3>
        <span className="nvp-method-basis">{basis}</span>
      </div>
      <p>{children}</p>
    </div>
  );
}

export default function Methodology({ fxRates }) {
  const rates = Object.entries(fxRates || {}).filter(([ccy]) => ccy !== "USD");
  return (
    <div className="nvp-method">
      <h3 className="nvp-method-section">Valuing the holdings</h3>
      <Row label="Listed shares" basis="Market price">
        Shares held times the latest price on or before the valuation date. A price set by hand on the Securities page
        is used first, then the prime broker’s closing price, then the daily market-data fetch. A holding with no price
        is carried at cost, and its row says so.
      </Row>
      <Row label="Convertible notes" basis="Cost">
        What the note cost, in its own currency, which is also what the ledger carries. A note bought at a discount
        converts at full face value, and the discount is booked as income on the day it converts; valuing the note at
        face as well would count that profit twice. The outstanding face is shown in its own column.
      </Row>
      <Row label="Convertible preferred" basis="Cost">
        What it cost, in its own currency. Dividends accrued but not paid are shown under accrued interest, not added
        here.
      </Row>
      <Row label="Loan notes held by the fund" basis="Cost less principal repaid">
        The fund’s loan notes with Loft Capital are carried at what was lent, less what has been repaid.
      </Row>
      <Row label="Loft Capital LP interest" basis="Cost until a statement arrives">
        An interest in a fund that reports quarterly. It is held at cost until Loft’s quarterly statement value is
        entered, dated to that quarter’s end.
      </Row>

      <h3 className="nvp-method-section">Cash and currencies</h3>
      <Row label="Cash" basis="Fund accounts only">
        The balances of the fund’s own bank accounts at Mercury and Citi, counting settled payments only, plus cash at
        the prime broker. Helena Partners Inc’s accounts are not the fund’s and are not counted.
      </Row>
      <Row label="Foreign currency" basis="Riksbank fixing">
        Every holding, cash balance and accrual converts to dollars at the Riksbank’s fixing for its currency on or
        before the valuation date.
        {rates.length > 0 && ` Rates in use (US dollars per unit): ${rates.map(([c, r]) => `${c} ${amount(r, 4)}`).join(", ")}.`}
      </Row>
      <Row label="Liabilities" basis="None recorded">
        Shares owed back to a counterparty would be valued at the same price as the shares held, so the two cancel.
        None are recorded: the Hilbert Group and Valuno Group collateral blocks were released on 20 October 2026.
      </Row>
      <Row label="Broker positions" basis="Cross-check only">
        The prime broker’s daily position file is a check on the book, not part of NAV. A position the broker shows and
        the book does not is a difference to reconcile, not an asset.
      </Row>

      <h3 className="nvp-method-section">Dates and history</h3>
      <Row label="A past valuation date" basis="Approximate">
        Today’s holdings, kept to those bought on or before the date and priced as at it. A holding sold since that
        date is missing, and one sold down since carries what is left of it, so a past date is a close guide rather
        than a restatement.
      </Row>
      <Row label="Net assets over time" basis="The ledger">
        The fund’s assets less what it owes, as its books held them at each month end and on every day a NAV was
        recorded. It goes back to the first entry and holds investments at cost. Marked NAV, which prices listed shares,
        exists only from September 2026, so it is drawn as its own line and never joined to the first.
      </Row>
      <Row label="Capital in and out" basis={`${money(CAPITAL_FLOOR_USD, { whole: true })}+ and ${CAPITAL_SHARE * 100}%+`}>
        Each point of the history says what moved net assets since the point before: profit, capital paid in less
        capital paid out, and currency translation on foreign-currency balances held. A move of capital is marked on the
        chart when it is at least {money(CAPITAL_FLOOR_USD, { whole: true })} and at least {CAPITAL_SHARE * 100}% of net assets.
      </Row>
      <Row label="Return" basis="Estimate">
        A change in net assets is not a return, because capital paid in and out moves it too. The estimate divides the
        gain — profit plus currency translation — by the average capital employed: opening net assets plus half of
        capital in less out. Where distributions paid out more than that, there is no average to divide by and the
        return reads n/a.
      </Row>

      <h3 className="nvp-method-section">Investors</h3>
      <Row label="Who the investors are" basis="Lenders">
        Every investor lends to Helena Partners Inc under a loan note for a named deal, and is repaid out of that deal’s
        proceeds. Their capital account is read from those notes.
      </Row>
      <Row label="Share of NAV" basis="Ownership on each deal">
        NAV is shared out by the ownership percentage recorded for each investor on each deal.
      </Row>
    </div>
  );
}
